import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Center,
  Container,
  Grid,
  Group,
  Loader,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  Clock,
  Edit3,
} from 'react-feather';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  preSessionReflectionApi,
  type PeerLeaderState,
  type PreSessionReflectionPayload,
  type ReflectionStatus,
  type VolunteerResponse,
} from '../../../services/api/preSessionReflection';
import { useFeatureFlag } from '../../../stores/featureFlags';
import { FeaturesDisabledPage } from '../../FeaturesDisabledPage';
import { FullContentHeader } from '../../../components/layout/FullContentHeader';
import { AiBanner } from './AiBanner';
import { PeerLeaderBanner } from './PeerLeaderBanner';
import { PriorCommitmentCard } from './PriorCommitmentCard';
import { ReadingTasksCard } from './ReadingTasksCard';
import { ReflectionFormCard } from './ReflectionFormCard';
import { SessionDescriptionBlock } from '../../../components/common/SessionDescriptionBlock';
import { SessionStartBanner } from '../../../components/participant/SessionStartBanner';
import { SessionDetailsCard } from './SessionDetailsCard';
import { YourGroupCard } from './YourGroupCard';
import { useReflectionAutosave } from './useReflectionAutosave';
import { composeSessionLabel } from '../sessionLabel';

// MED-813: poll cadence for real-time peer-leader banner transitions
// (States 2/3/6 → 4/5 when the CM reassigns). Mirrors the waiting-room pattern.
const PEER_LEADER_POLL_MS = 4_000;

// MED-1045: peer_leader states where the viewer is *leading* this session and
// therefore must NOT see the participant "Enter waiting room" CTA — the leader
// is the session initiator (prep → Start Session → active), not a waiting-room
// joiner. All other states (backup_needed/volunteer_pending/another_assigned/
// declined) and non-peer-leader participants keep the CTA. State 1 + State 5.
const ACTIVE_LEADER_STATES: ReadonlyArray<PeerLeaderState> = [
  'confirmed_leader',
  'backup_assigned',
];

/**
 * Pre-Session Reflection page (MED-803 / E3.1).
 *
 * Composition root for the participant pre-session reflection experience.
 * Loads the full payload from `GET /api/participant/sessions/:id/reflection`,
 * renders the header, conditional AI banner, reflection form (with autosave
 * + explicit Save), session details, and group cards.
 *
 * Gated by the `live_session_delivery` feature flag at both the API layer
 * (returns 404 when off) and the client layer (falls back to
 * FeaturesDisabledPage) for a consistent UX.
 */
export interface PreSessionReflectionPageProps {
  /**
   * MED-1090 — DEV-only static preview: seeds the reflection payload and
   * bypasses the feature-flag gate + fetch so the page renders from an
   * in-memory fixture (`/dev/session-prep/participant`). The dev route has no
   * `:sessionId`, so the autosave/volunteer handlers (which guard on
   * `sessionId`) no-op. Compiled out of production builds.
   */
  preview?: { payload: PreSessionReflectionPayload };
}

export function PreSessionReflectionPage({
  preview,
}: PreSessionReflectionPageProps = {}) {
  const isPreview = !!preview;
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const isFlagOn = useFeatureFlag('live_session_delivery');
  const [payload, setPayload] = useState<PreSessionReflectionPayload | null>(
    preview?.payload ?? null
  );
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState<string | null>(null);
  const [isVolunteering, setIsVolunteering] = useState(false);
  // MED-1168 — the assigned-but-declined leader (State 7) re-confirming in place.
  const [isReconfirming, setIsReconfirming] = useState(false);

  const fetchPayload = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!sessionId) return;
      const silent = opts?.silent ?? false;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const result = await preSessionReflectionApi.get(sessionId);
        if (silent) {
          // Real-time poll: merge ONLY the peer-leader block + leader info so an
          // in-flight reflection edit (autosave debounce) is never clobbered.
          setPayload(prev =>
            prev
              ? {
                  ...prev,
                  peer_leader: result.peer_leader,
                  session_details: result.session_details,
                }
              : result
          );
        } else {
          setPayload(result);
        }
      } catch (err) {
        if (silent) return; // ignore transient poll errors
        const e = err as Error & { status?: number };
        if (e?.status === 403) {
          setError('You are not enrolled in this session.');
        } else if (e?.status === 404) {
          setError('Session not found or unavailable.');
        } else if (e?.status === 410) {
          setError(
            'This reflection is closed because the session has started.'
          );
        } else {
          setError(e?.message ?? 'Failed to load reflection.');
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    if (!isPreview) void fetchPayload();
  }, [fetchPayload, isPreview]);

  // MED-813 (AC #14, IV6): while a peer-leader banner is active and the session
  // hasn't started, poll so a CM reassignment transitions the banner in
  // real-time (States 2/3/6 → 4/5) without a manual refresh.
  const peerLeaderActive = !!payload?.peer_leader && !payload?.is_locked;
  useEffect(() => {
    if (isPreview || !sessionId || !peerLeaderActive) return;
    const intervalId = window.setInterval(() => {
      void fetchPayload({ silent: true });
    }, PEER_LEADER_POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [isPreview, sessionId, peerLeaderActive, fetchPayload]);

  const handleVolunteer = useCallback(
    async (response: VolunteerResponse) => {
      if (!sessionId) return;
      setIsVolunteering(true);
      try {
        const result = await preSessionReflectionApi.volunteer(
          sessionId,
          response
        );
        setPayload(prev =>
          prev ? { ...prev, peer_leader: result.peer_leader } : prev
        );
      } catch (err) {
        const e = err as Error & { status?: number };
        notifications.show({
          color: 'red',
          title: 'Could not record your response',
          message: e?.message ?? 'Please try again.',
        });
      } finally {
        setIsVolunteering(false);
      }
    },
    [sessionId]
  );

  // MED-1168 — State 7 "Yes, I'll lead": re-confirm this leader's own RSVP in
  // place, then refetch. The recomputed peer_leader state (confirmed_leader /
  // backup_assigned) flips `isActiveSessionLeader` true below, so the render
  // redirects to the prep / Start Session page — the same destination a
  // never-declined leader reaches.
  const handleReconfirmLead = useCallback(async () => {
    if (!sessionId) return;
    setIsReconfirming(true);
    try {
      await preSessionReflectionApi.confirmLead(sessionId);
      await fetchPayload({ silent: true });
    } catch (err) {
      const e = err as Error & { status?: number };
      notifications.show({
        color: 'red',
        title: 'Could not confirm you as leader',
        message: e?.message ?? 'Please try again.',
      });
    } finally {
      setIsReconfirming(false);
    }
  }, [sessionId, fetchPayload]);

  const handleStatusChange = useCallback((status: ReflectionStatus) => {
    setPayload(prev => (prev ? { ...prev, status } : prev));
  }, []);

  const {
    state: autosaveState,
    scheduleSave,
    saveAllPending,
  } = useReflectionAutosave(sessionId, handleStatusChange);

  // MED-1180 — defense-in-depth: also disable when the per-cohort LSD flag is
  // false on the returned payload (backend already 404s a non-LSD cohort).
  // Never gate preview/dev routes (they render from fixtures).
  if (!isPreview && (!isFlagOn || payload?.live_session_delivery_enabled === false)) {
    return <FeaturesDisabledPage />;
  }

  if (loading) {
    return (
      <Center mih={400}>
        <Loader />
      </Center>
    );
  }

  if (error || !payload) {
    return (
      <Container size="md" py="lg">
        <Alert
          icon={<AlertTriangle size={18} />}
          color="red"
          title="Reflection unavailable"
        >
          <Text size="sm">{error ?? 'Unknown error.'}</Text>
        </Alert>
      </Container>
    );
  }

  // MED-1062 (E4.0h): the session was a no-show ('missed') and is pending
  // reschedule. Render a full "Session pending reschedule" state — neither the
  // reflection form nor the peer-leader "Start Session Prep" banner are shown
  // (the backend also nulls peer_leader in this state). Reachable via a
  // reflection email link or a stale tab after the no-show sweep runs.
  if (payload.is_missed) {
    return (
      <Container size="md" py="lg">
        <Alert
          color="warningAmber"
          icon={<Calendar size={18} />}
          title="Session pending reschedule"
        >
          <Stack gap="sm">
            <Text size="sm">
              This session was missed and hasn't been rescheduled yet. Your
              cohort manager will set a new time — anything you've already saved
              is kept and will be here when it's rescheduled.
            </Text>
            <Button
              variant="subtle"
              color="medleyGreen"
              w="fit-content"
              px={0}
              onClick={() => navigate('/participant')}
            >
              Back to your portal
            </Button>
          </Stack>
        </Alert>
      </Container>
    );
  }

  const handleSaveAll = async (
    pending: Array<{ question_id: string; response_text: string }>
  ) => {
    const status = await saveAllPending(pending);
    if (status) {
      setPayload(prev => (prev ? { ...prev, status } : prev));
    }
  };

  // MED-1115 — persist the Reading / Tasks reviewed marker, then update the
  // payload so the card shows the "Reviewed" check + stays collapsed. In preview
  // (no sessionId) optimistically stamp the marker locally so the flow works.
  const handleMarkReadingReviewed = async () => {
    if (isPreview || !sessionId) {
      setPayload(prev =>
        prev
          ? { ...prev, reading_tasks_reviewed_at: new Date().toISOString() }
          : prev
      );
      return;
    }
    const result = await preSessionReflectionApi.setReadingTasksReviewed(
      sessionId,
      true
    );
    setPayload(prev =>
      prev
        ? {
            ...prev,
            reading_tasks_reviewed_at: result.reading_tasks_reviewed_at,
          }
        : prev
    );
  };

  // MED-1045 (AC1/AC4/AC5): the viewer is "leading this session" only in
  // States 1 + 5. This single gate covers all three ACs — it suppresses the
  // waiting-room CTA for the active leader (AC1) while leaving it intact for
  // non-leader participants (AC4, peer_leader null) and for leaders who
  // declined / were replaced (AC5, states `declined` / `another_assigned`).
  const isActiveSessionLeader =
    !!payload.peer_leader &&
    ACTIVE_LEADER_STATES.includes(payload.peer_leader.state);

  // MED-1049 (E4.2b, AC1/AC2): the designated session leader who has not
  // declined (peer_leader state `confirmed_leader` or `backup_assigned` — the
  // same gate MED-1045 keys on) belongs on the unified prep page, where the
  // reflection and the context banner render exactly once. The dashboard
  // "Start Session Prep" CTAs and the leader "Starting Soon" banner route here
  // to `/reflection`, so funnel those non-declined leaders to the consolidated
  // page. Non-leaders, and a leader who declined / was replaced (any other
  // state, or peer_leader null), stay on this participant pre-session page.
  if (isActiveSessionLeader && sessionId) {
    return <Navigate to={`/participant/sessions/${sessionId}/prep`} replace />;
  }

  const saveHint =
    autosaveState === 'saving'
      ? 'Saving…'
      : autosaveState === 'saved'
        ? 'All changes saved.'
        : autosaveState === 'error'
          ? 'Save failed — try again.'
          : undefined;

  // MED-1127 (AC1/AC2/AC3) — prominent "join" banner at the top of the page,
  // above the fold, mirroring the dashboard banner. Participant-only by
  // construction: the active session leader was already redirected to /prep
  // above (MED-1049), so `isLeading` is always false here and the leader is
  // never offered the participant waiting room on this surface (AC9). The
  // banner renders only inside the live or starting-soon window; outside both,
  // nothing renders (AC3). The bottom "Enter waiting room" CTA stays as the
  // always-available fallback (AC4).
  const bannerState = payload.is_live
    ? 'live'
    : payload.starting_soon
      ? 'starting_soon'
      : null;

  // MED-1182 (E1.3f-R1 / AC7) — a BLANK session is agenda-complete but has no
  // pre-session work (no prompts, no reading/tasks, no commitment review). The
  // page still resolves on direct navigation, but degrades to a logistics-only
  // view: no reflection form, no reflection-specific chrome (AI-recording
  // banner), no reading/commitment cards. `is_incomplete` (agenda not ready)
  // keeps its own gray "not yet ready" branch below and takes precedence.
  const isBlank = !payload.is_incomplete && !payload.pre_session_work_present;

  // MED-1146 (AC-B1/AC-L1/AC-L2) — the one participant label rule: the
  // display-aware topic (else the group name) composed as "Session N: …", never
  // the raw `{group} - Session N` title. session_number / group_name come from
  // the payload's session_details / group blocks (no backend change — AC-B3).
  const sessionLabel = composeSessionLabel({
    session_number: payload.session_details.session_number,
    session_topic: payload.header.session_topic,
    group_name: payload.group.group_name,
    title: payload.header.session_title,
  });

  return (
    <>
      {/* MED-1090 (AC2) — shared page header (FullContentHeader), replacing the
          bespoke PreSessionHeader so the participant pre-session page matches the
          coach/peer prep pages. The server-computed timing badge ("In 2 days",
          "Today", …) sits inline beside the title. Rendered full-bleed; the body
          keeps its own Container below. */}
      <FullContentHeader
        data-testid="pre-session-header"
        sticky
        title={sessionLabel}
        programName={payload.header.program_name}
        clientName={payload.header.organization_name}
        durationMinutes={payload.header.duration_minutes}
        onBack={() =>
          window.history.length > 1 ? navigate(-1) : navigate('/participant')
        }
        accessory={
          payload.header.timing_badge ? (
            <Badge
              size="md"
              variant="light"
              color="blue"
              leftSection={<Calendar size={12} />}
              styles={{ label: { textTransform: 'none', fontWeight: 500 } }}
            >
              {payload.header.timing_badge}
            </Badge>
          ) : undefined
        }
      />
      <Container size="xl" py="lg">
        <Stack gap="lg">
          {/* MED-1127 — prominent live / starting-soon "join" banner at the top
              of the body, above the AI banner. Renders only inside the live or
              starting-soon window; participant-only (the active leader is
              redirected to /prep above, so isLeading is always false here). */}
          {bannerState && sessionId && (
            <SessionStartBanner
              state={bannerState}
              title={sessionLabel}
              isLeading={false}
              sessionId={sessionId}
              cohortId={payload.cohort_id}
            />
          )}
          {/* MED-1182 (AC7) — the AI-recording banner is reflection-specific
              chrome; suppress it on a blank session (nothing to reflect on). */}
          {!isBlank && payload.ai_features_enabled && payload.ai_listening_enabled && (
            <AiBanner />
          )}

          {/* MED-984 / E3.1c (AC-PO2): when the CM has not finished authoring
            the session's pre-session content (sections incomplete OR fewer
            than 2 reflection prompts), withhold the reflection content and
            show a gray "not yet ready" Alert. Mirrors the coach-prep
            "Prep not yet ready" pattern.

            Session Details + Group cards still render below so the
            participant has enough context to know which session this is. */}
          {payload.is_incomplete ? (
            <>
              <Alert
                color="gray"
                icon={<Clock size={16} />}
                title="Pre-session not yet ready"
              >
                <Text size="sm">
                  Your reflection unlocks once your facilitator finishes
                  preparing this session's content. Check back shortly.
                </Text>
              </Alert>

              {/* MED-1101 (AC2/AC3/AC6) — Session Description at the top of the
                content area, below the status alert. Hidden when unset (AC9). */}
              <SessionDescriptionBlock description={payload.description} />

              <Grid gutter="lg">
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <SessionDetailsCard
                    details={payload.session_details}
                    sessionTopic={payload.header.session_topic}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <YourGroupCard group={payload.group} />
                </Grid.Col>
              </Grid>
            </>
          ) : isBlank ? (
            <>
              {/* MED-1182 (E1.3f-R1 / AC7, AC9) — BLANK session: logistics-only
                  view. Friendly one-liner + the same context cards the
                  incomplete branch shows, but NO reflection form, NO AI banner
                  (suppressed above), and NO reading/commitment chrome. The page
                  still resolves on direct navigation; it's just not advertised
                  (the dashboard withholds its CTA — AC8). */}
              <Alert
                color="medleyGreen"
                variant="light"
                icon={<Calendar size={16} />}
                title="You're all set"
                data-testid="pre-session-blank-state"
              >
                <Text size="sm">
                  Nothing to prepare before this session — see you there!
                </Text>
              </Alert>

              <SessionDescriptionBlock description={payload.description} />

              <Grid gutter="lg">
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <SessionDetailsCard
                    details={payload.session_details}
                    sessionTopic={payload.header.session_topic}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <YourGroupCard group={payload.group} />
                </Grid.Col>
              </Grid>
            </>
          ) : (
            <>
              {/* MED-813 / E4.1: peer-leader banner sits above the reflection section. */}
              {payload.peer_leader && sessionId && (
                <PeerLeaderBanner
                  block={payload.peer_leader}
                  sessionId={sessionId}
                  onVolunteer={handleVolunteer}
                  isSubmitting={isVolunteering}
                  onReconfirmLead={handleReconfirmLead}
                  isReconfirming={isReconfirming}
                />
              )}

              {/* MED-1101 (AC2/AC3/AC6) — Session Description at the top of the
                content area, below the peer-led status banner and above the
                reflection/commitment cards. Hidden when unset (AC9). Source:
                session_number_config.description (AC7). */}
              <SessionDescriptionBlock description={payload.description} />

              {/* MED-813 AC #8: State 4 reflection reminder, just above the reflection form. */}
              {payload.peer_leader?.state === 'another_assigned' && (
                <Alert
                  icon={<Edit3 size={18} />}
                  color="medleyGreen"
                  variant="light"
                  title="Don't forget your reflection"
                >
                  <Text size="sm">
                    Please make sure to review and submit your reflection before
                    the session starts.
                  </Text>
                </Alert>
              )}

              {/* MED-937 / E3.1b: prior-commitment "Commitment Review" section.
                Renders only when payload.prior_commitment is non-null. The API
                applies three gates: the group coaching modality's
                commitment_tracking_enabled (relocated from the cohort in
                MED-1069 / E1.10c), non-empty prior commitment, AND the
                CM-authored pre-session-work.commitment_review_enabled toggle
                (added in MED-984 / E3.1c AC-CR1). */}
              {payload.prior_commitment && sessionId && (
                <PriorCommitmentCard
                  sessionId={sessionId}
                  block={payload.prior_commitment}
                  isLocked={payload.is_locked}
                />
              )}

              <ReflectionFormCard
                questions={payload.questions}
                status={payload.status}
                estimatedMinutes={payload.estimated_minutes}
                isLocked={payload.is_locked}
                onChange={scheduleSave}
                onSaveAll={handleSaveAll}
                saveHint={saveHint}
              />

              {/* MED-984 / E3.1c (AC-RT1, AC-PO1): authored Reading / Tasks
                content, positioned last in the section order ("optional, shown
                last as bonus when present"). AC-RT2: omitted entirely when
                payload.reading_tasks is null/empty. */}
              {payload.reading_tasks && (
                <ReadingTasksCard
                  content={payload.reading_tasks}
                  reviewedAt={payload.reading_tasks_reviewed_at}
                  onMarkReviewed={handleMarkReadingReviewed}
                />
              )}

              <Grid gutter="lg">
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <SessionDetailsCard
                    details={payload.session_details}
                    sessionTopic={payload.header.session_topic}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <YourGroupCard group={payload.group} />
                </Grid.Col>
              </Grid>

              {/* MED-804: enter the waiting room. Participant remains on the
                reflection page until they explicitly opt in — AI disclosure
                happens after the navigate, inside the waiting room itself.

                MED-1045: the active session leader (peer_leader state
                `confirmed_leader` or `backup_assigned`) is the session
                initiator, not a waiting-room joiner — their forward path is the
                "Start Session Prep" banner button → prep → Start Session. Hide
                the CTA for them while keeping it for everyone else (non-leader
                participants and leaders who declined / were replaced). */}
              {!isActiveSessionLeader && (
                <Group justify="flex-end">
                  <Button
                    color="medleyGreen"
                    rightSection={<ArrowRight size={16} />}
                    onClick={() =>
                      sessionId &&
                      navigate(
                        `/participant/sessions/${sessionId}/waiting-room`
                      )
                    }
                    data-testid="enter-waiting-room"
                  >
                    Enter waiting room
                  </Button>
                </Group>
              )}
            </>
          )}
        </Stack>
      </Container>
    </>
  );
}
